﻿import { SessionService } from "./session";
import { FIQAssist } from "../app";
import { Utils, DateTime } from "@fiqas/utils";
import { Injectable, Inject } from "@fiqas/angular-utils";
import { AuthenticationApiService, AccessTokenResponse } from "./authenticationApiService";
import { IsProcessingService } from "./isProcessingService";
import { FiqassistRole, User } from "../models/user";
import { AuthenticationService } from "./authenticationService";
import { StartResult } from "../models/home";
import { FqsService } from "./fqsService";
import { FiqassistApiURL } from "../constants";
import { ParsedResult } from "../models/base";
import * as Rx from "rxjs/Rx";
import { Observable } from "rxjs/Observable";
import { Subscription } from "rxjs/Subscription";
import { SaveDialog } from "../components/general/savedialog";

const ROLEKEY: string = "AuthorizationCurrentRoleKey";

@Injectable({
    module: FIQAssist,
    selector: AuthorizationService.selector
})
export class AuthorizationService extends FqsService {
    public static selector = "AuthorizationService"
    private roleChangedSubject: Rx.Subject<{ newRole: FiqassistRole, oldRole: FiqassistRole }>;

    constructor(
        @Inject() private saveDialog: SaveDialog,
        @Inject('$q') $q: angular.IQService,
        @Inject('$http') $http: angular.IHttpService,
        @Inject() private authenticationService: AuthenticationService,
        @Inject() private session: SessionService,
        @Inject('FiqassistApiURL') private FiqassistApiURL: FiqassistApiURL,
    ) {
        super($q, $http);
        this.doSetBearer(c => this.authenticationService.AuthenticationSetter(c))
        this.authenticationService.addLoginHandler((sender, username: string) => this.init(username));
        this.authenticationService.addLogoutHandler(() => {;
            this._lowrole = null;
            this.session.clear(ROLEKEY);
            this._highrole = null;
            this._roles = null;
        });
        this.roleChangedSubject = new Rx.Subject<{ newRole: FiqassistRole, oldRole: FiqassistRole }>();
        this.init(this.authenticationService.username);
    }
    public get loaded(): boolean {
        return this.currentRole != undefined && this.roles != undefined;
    }
    private initializing = false;
    private _onLoaded: ng.IQResolveReject<void>;
    public roleLoaded: angular.IPromise<void>;
    private init(username: string) {
        if (this.loaded) {
            this._onLoaded();
        }
        if (this.initializing || this.loaded) {
            return;
        }
        this.roleLoaded = this.$q<void>(resolver => this._onLoaded = resolver);
        this.initializing = true;
        if (username) {
            this.start(username).then(
                (user) => {
                    let roles = user.authorizedFiqassistRoles;
                    if (roles) {
                        this._roles = roles;
                        if (this._roles) {
                            this._lowrole = roles[0];
                            this._highrole = roles[roles.length - 1];

                            if (this.currentRole == null || roles.indexOf(this.currentRole) == -1) {
                                this.session.setValue(ROLEKEY, roles.filter(role => role.toUpperCase() === user.fiqassistRole.toUpperCase())[0], false, false);
                                this.roleChangedSubject.next({ newRole: this.currentRole, oldRole: undefined });
                            }
                        } else {
                            this._lowrole = null;
                            this.session.clear(ROLEKEY);
                            this._highrole = null;
                        }
                    } else {
                        this._lowrole = null;
                        this.session.clear(ROLEKEY);
                        this._highrole = null;
                        this._roles = null;
                    }
                    this._onLoaded();
                    this.initializing = false;
                },
                (reject) => {
                    this._onLoaded();
                    this.initializing = false;
                }
            );
        } else {
            this._lowrole = null;
            this.session.clear(ROLEKEY);
            this._highrole = null;
            this._roles = null;
            this.initializing = false;
            this._onLoaded();
        }
    }

    public static RoleCheck = function RoleCheck(authenticationService: AuthenticationService, $rootRouter: angular.Router, $location: angular.ILocationService, IsProcessingService: IsProcessingService, AuthorizationService: AuthorizationService) {
        return AuthenticationService.AuthenticationCheck(authenticationService, $rootRouter, $location, IsProcessingService);
    }

    // check credentials lifetime before api calls
    public RoleHeaderSetter(config: ng.IRequestConfig): ng.IPromise<ng.IRequestConfig> {
        return this.$q<ng.IRequestConfig>(resolver => resolver(this.setRoleHeader(config)));
    }

    private setRoleHeader(config: ng.IRequestConfig): ng.IRequestConfig {
        if (!!this.currentRole) {
            config.headers["Fiqassist-Role"] = `${this.currentRole}`;
        }
        return config;
    }

    public currentRoleIn(roles: FiqassistRole[]) {
        return roles.some(r => r == this.currentRole);
    }

    public get currentRole(): FiqassistRole {
        return this.session.getValue(ROLEKEY) as FiqassistRole;
    }
    private _lowrole: FiqassistRole;
    public get lowRole(): FiqassistRole {
        return this._lowrole;
    }
    private _highrole: FiqassistRole;
    public get highRole(): FiqassistRole {
        return this._highrole;
    }
    private _roles: FiqassistRole[];
    public get roles(): FiqassistRole[] {
        return this._roles;
    }

    public dirty: boolean = false;
    public switchRole(role: FiqassistRole | boolean) {
        if (this.dirty) {
            try {
                this.saveDialog.Save().then(
                    (response) => {
                        if (response === SaveDialog.Discard) {
                            this.dirty = false;
                            this.switchRole(role);
                        }
                    }
                );
            } finally {
                this._elevated =  this._highrole === this.currentRole;
                this.elevated = null; // trigger get binders
                return;
            }
        }

        var old = this.currentRole;
        if (role === false) {
            if (this.currentRole !== this._lowrole) {
                this.session.setValue(ROLEKEY, this._lowrole, false, false);
                this.roleChangedSubject.next({ newRole: this.currentRole, oldRole: old });
            }
        } else if (role === true) {
            if (this.currentRole !== this._highrole) {
                this.session.setValue(ROLEKEY, this._highrole, false, false);
                this.roleChangedSubject.next({ newRole: this.currentRole, oldRole: old });
            }
        } else if (this._roles && this._roles.length > 0 && this._roles.indexOf(role) >= 0) {
            if (this.currentRole !== role) {
                this.session.setValue(ROLEKEY, role, false, false);
                this.roleChangedSubject.next({ newRole: this.currentRole, oldRole: old });
            }
        } else {
            throw new Error("Role not found");
        }
        this._elevated =  this._highrole === this.currentRole;
        this.elevated = null; // trigger get binders
    }
    private _elevated: boolean = false;
    public get elevated(): boolean { return this._elevated; }
    public set elevated(val: boolean) { }

    public onRoleChanged(onNext: (next: { newRole: FiqassistRole, oldRole: FiqassistRole }) => void): Subscription {
        return this.roleChangedSubject.subscribe(onNext);
    }
    public start(user: string) {
        return this.performRequest<void, User>("GET", `${this.FiqassistApiURL}/user/${user}`);
    }

    public get isFiqasEmployee(): boolean {
        return this.currentRoleIn(["SD", "FQS"]);
    }
}
AuthorizationService.RoleCheck.$inject = ['AuthenticationService', '$rootRouter', '$location', 'IsProcessingService', 'AuthorizationService'];
